import { useCallback, useEffect, useState } from 'react';

// material
import {
  Card,
  Table,
  Checkbox,
  TableRow,
  TableBody,
  TableCell,
  Container,
  TableContainer,
  Stack,
  Link,
  Chip,
  Toolbar,
  Tooltip,
  IconButton,
  Input,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import Pagination from '@mui/material/Pagination';

// components
import Iconify from '../../../components/Iconify';
import Page from '../../../components/Page';
import Scrollbar from '../../../components/Scrollbar';
import SearchNotFound from '../../../components/SearchNotFound';
import HeaderBreadcrumbs from '../../../components/HeaderBreadcrumbs';
import * as ROUTES from '../../../constants/routes';
import { UserListHead } from '../../../sections/@dashboard/user';
import { useDidMount, useSettings } from '../../../hooks';
import { fDateTime } from '../../../utils/formatTime';
import TableLoadingRow from '../../../components/TableLoadingRow';

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: 'to_name', label: 'Name', alignRight: false },
  { id: 'to_email', label: 'Email', alignRight: false },
  { id: 'school_name', label: 'School', alignRight: false },
  { id: 'school_contact_role', label: 'Role', alignRight: false },
  { id: 'download_url', label: 'Download URL', alignRight: false },
  { id: 'email_sent', label: 'Email Sent', alignRight: false },
  { id: 'sent_at', label: 'Sent At', alignRight: false },
  { id: 'created_at', label: 'Created At', alignRight: false },
];

// ----------------------------------------------------------------------

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });

  if (query) {
    const lowerQuery = query.toLowerCase();
    return array.filter(
      (item) =>
        item.to_name?.toLowerCase().includes(lowerQuery) ||
        item.to_email?.toLowerCase().includes(lowerQuery) ||
        item.school_name?.toLowerCase().includes(lowerQuery) ||
        item.school_contact_role?.toLowerCase().includes(lowerQuery)
    );
  }
  return stabilizedThis.map((el) => el[0]);
}

// ----------------------------------------------------------------------

export default function KitEmailRequestsList() {
  const [page, setPage] = useState(1);
  const [requestsList, setRequestsList] = useState([]);
  const [requests, setRequests] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const isDidMount = useDidMount();
  const [order, setOrder] = useState('desc');
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState('created_at');
  const [filterName, setFilterName] = useState('');
  const { themeStretch } = useSettings();
  const rowsPerPage = 20;

  const fetchRequests = useCallback(
    async (pageNumber, rows) => {
      try {
        const authToken = JSON.parse(localStorage.getItem(process.env.REACT_APP_ADMIN_AUTH_TOKEN));
        const requestOptions = {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: `Bearer ${authToken.token}`,
          },
        };

        const params = {
          per_page: rows,
          page: pageNumber,
        };

        const queryString = new URLSearchParams(params).toString();
        const url = `${ROUTES.ADMIN_API}/kit-email-requests?${queryString}`;

        if (isDidMount) {
          setLoading(true);
          const _res = await fetch(url, requestOptions)
            .then((res) => res.json())
            .then((data) => data)
            .catch((err) => {
              console.log(err);
              return null;
            });

          if (!_res?.data) {
            if (isDidMount) {
              setLoading(false);
            }
          } else {
            const data = _res;
            if (isDidMount) {
              setLoading(false);
              setRequestsList(data);
              const mapped = data?.data?.data.map((item) => ({
                id: item?.id,
                to_name: item?.to_name || 'N/A',
                to_email: item?.to_email || 'N/A',
                school_name: item?.school_name || 'N/A',
                school_contact_role: item?.school_contact_role || 'N/A',
                download_url: item?.download_url || 'N/A',
                email_sent: item?.email_sent,
                sent_at: item?.sent_at,
                created_at: item?.created_at,
              }));
              setRequests(mapped);
            }
          }
        }
      } catch (e) {
        if (isDidMount) {
          setLoading(false);
          console.log(e);
        }
      }
    },
    [isDidMount]
  );

  useEffect(() => {
    fetchRequests(page, rowsPerPage);
  }, [fetchRequests, page]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = requests.map((n) => n.id);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
    }
    setSelected(newSelected);
  };

  const handleChange = (event, value) => {
    setPage(value);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
  };

  const emptyRows = page > 1 ? Math.max(0, (1 + page) * rowsPerPage - requestsList?.data?.data.length) : 0;

  const _requestsList = requestsList?.data ? requests : [];
  const filteredRequests = applySortFilter(_requestsList, getComparator(order, orderBy), filterName);
  const isRequestNotFound = !filteredRequests.length;

  const handleExportBtnClick = () => {
    const csvHeader = ['ID', 'Name', 'Email', 'School', 'Role', 'Download URL', 'Email Sent', 'Sent At', 'Created At'];
    const csvRows = filteredRequests.map((row) => [
      row.id,
      row.to_name,
      row.to_email,
      row.school_name,
      row.school_contact_role,
      row.download_url,
      row.email_sent ? 'Yes' : 'No',
      row.sent_at ? fDateTime(row.sent_at) : '-',
      row.created_at ? fDateTime(row.created_at) : '-',
    ]);

    const csvContent = [csvHeader.join(','), ...csvRows.map((r) => r.map((field) => `"${String(field).replace(/"/g, '""')}"`).join(','))].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `kit-email-requests-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
  };

  return (
    <Page title="Kit Email Requests">
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading="School Fee Collection Kit Downloads"
          links={[
            { name: 'Dashboard', href: ROUTES.PATH_ADMIN.root },
            { name: 'Growth Retention', href: ROUTES.PATH_ADMIN.growthRetention.root },
            { name: 'Kit Downloads' },
          ]}
        />

        <Card>
          <Scrollbar>
            <Toolbar sx={{ height: 96, pt: 3, pb: 1, px: { xs: 0, md: 1 } }}>
              <Tooltip title="Filter List">
                <IconButton>
                  <Iconify icon="ic:round-filter-list" />
                </IconButton>
              </Tooltip>
              <Stack spacing={4} direction="row" sx={{ px: 2.5 }}>
                <Input
                  id="search-kit"
                  margin="dense"
                  placeholder="Search name, email, school, role"
                  value={filterName}
                  onChange={handleFilterByName}
                  sx={{ minWidth: 260 }}
                />
                <LoadingButton loading={isLoading} onClick={handleExportBtnClick}>
                  Export Spreadsheet
                </LoadingButton>
              </Stack>
            </Toolbar>
          </Scrollbar>

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <UserListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={requestsList?.data?.data.length}
                  numSelected={selected.length}
                  onRequestSort={handleRequestSort}
                  onSelectAllClick={handleSelectAllClick}
                />
                {isLoading || requestsList.length === 0 ? (
                  <TableBody>
                    <TableLoadingRow colSpan={9} isLoading={isLoading} />
                  </TableBody>
                ) : (
                  <TableBody>
                    {filteredRequests.slice().map((row) => {
                      const isItemSelected = selected.indexOf(row.id) !== -1;

                      return (
                        <TableRow
                          hover
                          key={row.id}
                          tabIndex={-1}
                          role="checkbox"
                          selected={isItemSelected}
                          aria-checked={isItemSelected}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox checked={isItemSelected} onChange={() => handleClick(row.id)} />
                          </TableCell>
                          <TableCell align="left">{row.to_name}</TableCell>
                          <TableCell align="left">{row.to_email}</TableCell>
                          <TableCell align="left">{row.school_name}</TableCell>
                          <TableCell align="left">
                            <Chip label={row.school_contact_role} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell align="left">
                            <Link
                              href={row.download_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              variant="caption"
                              sx={{ wordBreak: 'break-all' }}
                            >
                              {row.download_url}
                            </Link>
                          </TableCell>
                          <TableCell align="left">
                            <Chip
                              label={row.email_sent ? 'Sent' : 'Failed'}
                              color={row.email_sent ? 'success' : 'error'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="left">{row.sent_at ? fDateTime(row.sent_at) : '-'}</TableCell>
                          <TableCell align="left">{row.created_at ? fDateTime(row.created_at) : '-'}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                )}

                {isRequestNotFound && !isLoading && emptyRows > 0 && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={9} sx={{ py: 3 }}>
                        <SearchNotFound searchQuery={filterName} />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>
          <Pagination
            count={requestsList?.data ? requestsList?.data?.last_page : 0}
            page={page}
            onChange={handleChange}
          />
        </Card>
      </Container>
    </Page>
  );
}
